import { type FC, useCallback, useEffect, useState } from "react";

import {
  activateRepositoryWritesAfterReview,
  fetchPushBrokerAllowlistReview,
  fetchPushBrokerOverview,
  type PushBrokerAllowlistItem,
  type PushBrokerAllowlistReview,
  type PushBrokerOverview,
  reauthorizeRepositoryWrites,
  revokeRepositoryWrites,
} from "../../api/pushBroker";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { isAdmin } from "../../utils/rbac";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { ChecklistItem } from "../common/JiraSetupView";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";

interface Props {
  providerId: string;
  /** Repositories connected in Arti — used to resolve allowlist slugs. */
  repositorySlugsById?: ReadonlyMap<string, string>;
}

/**
 * Push broker operator card (single-app consolidation: the broker mints
 * from the SAME GitHub App as the read plane). The push-plane installation
 * is the existing App A installation recorded in the provider row metadata —
 * no registration surface exists.
 *
 * Two distinct switches live behind "push":
 * - **Repository write authorization** (this card, org-wide): the allowlist
 *   row the broker gates every mint on. Activated for a repository the first
 *   time a workspace creates a branch on it; org admins review, revoke and
 *   re-authorize here (R2.4/R2.5).
 * - **Attachment push** (workspace repository rows): the per-attachment
 *   toggle. Turning it off never revokes the org-wide authorization.
 */
export const PushBrokerCard: FC<Props> = ({
  providerId,
  repositorySlugsById,
}) => {
  const auth = useDashboardAuth();
  const canAdminister = isAdmin(auth.orgRole, auth.globalRole);
  const [overview, setOverview] = useState<PushBrokerOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [reviewRepositoryId, setReviewRepositoryId] = useState<string>("");
  const [review, setReview] = useState<PushBrokerAllowlistReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setOverview(await fetchPushBrokerOverview(providerId));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!reviewRepositoryId) {
      setReview(null);
      return undefined;
    }
    let cancelled = false;
    setReviewLoading(true);
    setReview(null);
    setActionError(null);
    void fetchPushBrokerAllowlistReview({
      providerId,
      repositoryId: reviewRepositoryId,
    })
      .then((result) => {
        if (!cancelled) {
          setReview(result);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setActionError(
            error instanceof Error ? error.message : String(error),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReviewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [providerId, reviewRepositoryId]);

  const runAdminAction = async (
    key: string,
    action: () => Promise<PushBrokerAllowlistItem>,
  ) => {
    setBusyKey(key);
    setActionError(null);
    try {
      await action();
      setReviewRepositoryId("");
      await reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusyKey(null);
    }
  };

  const allowlist = (overview?.allowlist ?? []).filter(
    (row) => row.providerId === providerId,
  );
  const activeAllowlist = allowlist.filter((row) => row.status === "active");
  const revokedAllowlist = allowlist.filter((row) => row.status === "revoked");
  const activeIds = new Set(activeAllowlist.map((row) => row.repositoryId));
  const reviewableRepositories = [...(repositorySlugsById ?? [])].filter(
    ([repositoryId]) => !activeIds.has(repositoryId),
  );
  const slug = (repositoryId: string) =>
    repositorySlugsById?.get(repositoryId) ?? repositoryId;

  return (
    <PanelSection title="Push broker">
      <div className="space-y-4">
        <div className="rounded-none border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/60">
          <Text className="leading-relaxed text-slate-600 dark:text-slate-300">
            The push broker mints single-repo, short-lived write tokens from the
            GitHub App and hands them only to the host-side push sidecar —
            actors never hold a write credential. Repository writes are
            authorized organization-wide here; each workspace then turns push on
            or off per attachment from its repository list.
          </Text>
        </div>

        {loadError && <Alert variant="warning">{loadError}</Alert>}
        {actionError && <Alert variant="error">{actionError}</Alert>}
        {loading && !overview && (
          <Text variant="label" className="text-gray-400 dark:text-white/40">
            Loading push broker state…
          </Text>
        )}

        {overview && (
          <div className="space-y-3">
            <ChecklistItem
              label="Repositories authorized for writes"
              detail={
                activeAllowlist.length > 0
                  ? `${activeAllowlist.length} active`
                  : "None — creating a branch from a workspace authorizes its repository"
              }
              ok={activeAllowlist.length > 0}
            />
            {activeAllowlist.length > 0 && (
              <ul className="space-y-1">
                {activeAllowlist.map((row) => (
                  <li
                    key={row.repositoryId}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <Badge variant="status" size="sm">
                      {slug(row.repositoryId)}
                    </Badge>
                    <Text
                      variant="label"
                      className="text-gray-400 dark:text-white/40"
                    >
                      by {row.allowedBy ?? "unknown"}
                    </Text>
                    {canAdminister && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={busyKey === `revoke:${row.repositoryId}`}
                        disabled={busyKey !== null}
                        onClick={() =>
                          void runAdminAction(
                            `revoke:${row.repositoryId}`,
                            () =>
                              revokeRepositoryWrites({
                                providerId,
                                repositoryId: row.repositoryId,
                              }),
                          )
                        }
                      >
                        Revoke
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {canAdminister && revokedAllowlist.length > 0 && (
              <div className="space-y-1">
                <Text
                  variant="label"
                  className="text-gray-400 dark:text-white/40"
                >
                  Revoked — every push to these repositories is refused
                </Text>
                <ul className="space-y-1">
                  {revokedAllowlist.map((row) => (
                    <RevokedRepositoryRow
                      key={row.repositoryId}
                      row={row}
                      slug={slug}
                      busyKey={busyKey}
                      runAdminAction={runAdminAction}
                      providerId={providerId}
                    />
                  ))}
                </ul>
              </div>
            )}

            {canAdminister && reviewableRepositories.length > 0 && (
              <div className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-800">
                <Text
                  variant="label"
                  className="text-gray-400 dark:text-white/40"
                >
                  Review &amp; activate repository writes
                </Text>
                <Text className="text-slate-600 dark:text-slate-300">
                  A workspace may be refused when activating writes would also
                  unlock push in other workspaces that still have it enabled.
                  Review those here, then activate for the whole organization.
                </Text>
                <select
                  value={reviewRepositoryId}
                  onChange={(event) =>
                    setReviewRepositoryId(event.currentTarget.value)
                  }
                  className="w-full border border-slate-300 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                >
                  <option value="">Select a repository…</option>
                  {reviewableRepositories.map(([repositoryId, remoteSlug]) => (
                    <option key={repositoryId} value={repositoryId}>
                      {remoteSlug}
                    </option>
                  ))}
                </select>
                {reviewLoading && (
                  <Text
                    variant="label"
                    className="text-gray-400 dark:text-white/40"
                  >
                    Loading enabled policies…
                  </Text>
                )}
                {review && reviewRepositoryId && (
                  <div className="space-y-2">
                    <ReviewSummary review={review} />
                    {review.allowlist?.status !== "revoked" && (
                      <Button
                        size="sm"
                        loading={busyKey === `activate:${reviewRepositoryId}`}
                        disabled={busyKey !== null}
                        onClick={() =>
                          void runAdminAction(
                            `activate:${reviewRepositoryId}`,
                            () =>
                              activateRepositoryWritesAfterReview({
                                providerId,
                                repositoryId: reviewRepositoryId,
                              }),
                          )
                        }
                      >
                        {review.enabledPolicies.length > 0
                          ? `Activate organization-wide (unlocks ${review.enabledPolicies.length})`
                          : "Activate organization-wide"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PanelSection>
  );
};

function ReviewSummary({ review }: { review: PushBrokerAllowlistReview }) {
  if (review.allowlist?.status === "revoked") {
    return (
      <Alert variant="warning">
        Writes to this repository were revoked
        {review.allowlist.revokedBy ? ` by ${review.allowlist.revokedBy}` : ""}.
        Use Re-authorize above instead of activating.
      </Alert>
    );
  }
  if (review.enabledPolicies.length === 0) {
    return (
      <Text className="text-slate-600 dark:text-slate-300">
        No workspace currently has push enabled for this repository. Activation
        unlocks nothing today; future workspaces gain push only when their
        toggle is on.
      </Text>
    );
  }
  return (
    <ul className="space-y-1">
      {review.enabledPolicies.map((policy) => (
        <li
          key={policy.channelEnvironmentId}
          className="font-mono text-[11px] text-slate-700 dark:text-slate-200"
        >
          {policy.channelName ?? policy.channelEnvironmentId}
          {policy.attachmentBranch ? ` · ${policy.attachmentBranch}` : ""}
          {policy.attachmentStatus ? ` · ${policy.attachmentStatus}` : ""}
          {policy.updatedBy ? ` · enabled by ${policy.updatedBy}` : ""}
        </li>
      ))}
    </ul>
  );
}

interface RevokedRepositoryRowProps {
  row: PushBrokerAllowlistItem;
  slug: (id: string) => string;
  busyKey: string | null;
  runAdminAction: (
    key: string,
    action: () => Promise<PushBrokerAllowlistItem>,
  ) => Promise<void>;
  providerId: string;
}

function RevokedRepositoryRow({
  row,
  slug,
  busyKey,
  runAdminAction,
  providerId,
}: RevokedRepositoryRowProps) {
  return (
    <li key={row.repositoryId} className="flex flex-wrap items-center gap-2">
      <Badge variant="status" size="sm">
        {slug(row.repositoryId)}
      </Badge>
      <Text variant="label" className="text-gray-400 dark:text-white/40">
        revoked by {row.revokedBy ?? "unknown"}
      </Text>
      <Button
        size="sm"
        variant="secondary"
        loading={busyKey === `reauthorize:${row.repositoryId}`}
        disabled={busyKey !== null}
        onClick={() =>
          void runAdminAction(`reauthorize:${row.repositoryId}`, () =>
            reauthorizeRepositoryWrites({
              providerId,
              repositoryId: row.repositoryId,
            }),
          )
        }
      >
        Re-authorize
      </Button>
    </li>
  );
}
